import { breadcrumbAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe, type SystemStyleObject } from '@chakra-ui/react/styled-system';

const linkStyle: SystemStyleObject = {
  cursor: 'pointer',
  color: 'text/link',
  textStyle: 'comp/link/md',
  _hover: {
    color: 'text/link-hover',
    textStyle: 'comp/link/md-hover',
    _active: {
      color: 'text/primary',
      textStyle: 'comp/link/md-hover',
    },
  },
  _active: {
    color: 'text/primary',
    textStyle: 'comp/link/md-hover',
  },
  _focusVisible: {
    borderRadius: '2',
    background: 'background/primary',
    backgroundBlendMode: 'multiply',
  },
  _disabled: {
    color: 'text/disabled',
    outline: 'none',
    pointerEvents: 'none',
  },
};

// A crumb that outgrows the trail is cut off by whatever is above it, with nothing to say it was cut.
// `truncate` is Chakra's own utility for the three properties v1 carried, but it is not enough on its
// own here: an anchor is inline by default, where overflow does not clip at all, and bitkit builds its
// system from `defaultBaseConfig`, which ships no component recipes -- so Chakra's own
// `display: inline-flex` for this slot never arrives. Measured against the alternatives, the
// shrink-to-fit displays (inline-block, inline-flex) size to the text and never give way; only block
// takes the width its item was shrunk to.
const truncateStyle: SystemStyleObject = {
  display: 'block',
  truncate: true,
};

const breadcrumbSlotRecipe = defineSlotRecipe({
  className: 'breadcrumb',
  slots: breadcrumbAnatomy.keys(),
  base: {
    list: {
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
      color: 'text/tertiary',
      gap: '4',
    },
    // A flex item refuses to shrink below its content, so without this the trail pushes its
    // container instead of truncating. Only the last crumb gives way: the ancestors above it are
    // short and stay readable, and the deepest one is the name that actually runs long.
    //
    // "The item with no item after it" rather than `_last`: a separator is an `<li>` of its own, and
    // the mobile trail ends with one, so the last child there is a separator and `:last-of-type`
    // picks nothing. The slot class is the only thing that tells the two apart -- Chakra emits
    // `breadcrumb__item` from this recipe's own `className`, and no `data-part`.
    item: {
      '&:not(:has(~ .breadcrumb__item))': {
        minWidth: 0,
      },
    },
    link: {
      ...linkStyle,
      ...truncateStyle,
    },
    currentLink: {
      color: 'text/body',
      textStyle: 'body/md/semibold',
      ...truncateStyle,
    },
    separator: {
      color: 'icon/disabled',
    },
    ellipsis: {
      ...linkStyle,
    },
  },
});

export default breadcrumbSlotRecipe;
